import type { CallLogContactRef, CallLogDto } from "../../services/telephonyService";

/**
 * Mehrere Kontakte je Anruf (PO 28.09.2026) — reine Ableitungen für Liste,
 * Detail und Kontakt-Akte.
 *
 * Das Backend liefert `contacts` mit dem Hauptkontakt zuerst; weitere Kontakte
 * nur, soweit der Benutzer sie sehen darf. Ältere Antworten (oder ein Anruf,
 * der gerade lokal geändert wurde) haben das Feld evtl. nicht — dann gilt der
 * Hauptkontakt aus `contactId`/`contactDisplayName` allein.
 */

/** Alle Kontakte des Anrufs, Hauptkontakt zuerst. */
export function kontakteDesAnrufs(call: CallLogDto): CallLogContactRef[] {
    if (Array.isArray(call.contacts) && call.contacts.length > 0) {
        const haupt = call.contacts.filter((k) => k.isPrimary);
        const weitere = call.contacts.filter((k) => !k.isPrimary);
        return [...haupt, ...weitere];
    }
    return call.contactId
        ? [{ id: call.contactId, displayName: call.contactDisplayName ?? null, isPrimary: true }]
        : [];
}

/** Nur die weiteren Kontakte (ohne Hauptkontakt). */
export function weitereKontakte(call: CallLogDto): CallLogContactRef[] {
    return kontakteDesAnrufs(call).filter((k) => !k.isPrimary && k.id !== call.contactId);
}

/** Anzeigename eines Kontakts mit Rückfall auf die Nummer. */
export function kontaktName(k: CallLogContactRef): string {
    return k.displayName?.trim() || `Kontakt #${k.id}`;
}

/**
 * „+2" hinter dem Hauptkontakt in der Anrufzeile, samt Tooltip mit den Namen
 * der weiteren Kontakte. `null`, wenn es keine weiteren gibt.
 */
export function weitereKontakteHinweis(call: CallLogDto): { text: string; title: string } | null {
    const weitere = weitereKontakte(call);
    if (weitere.length === 0) return null;
    return {
        text: `+${weitere.length}`,
        title: `Weitere Kontakte: ${weitere.map(kontaktName).join(", ")}`,
    };
}

/**
 * Steht der Anruf in der Akte dieses Kontakts nur, weil der Kontakt als
 * WEITERER Kontakt daran hängt? Dann trägt die Zeile den Namen eines anderen
 * (des Hauptkontakts) — ohne Hinweis sähe das wie ein Fehler aus.
 */
export function alsWeitererKontakt(call: CallLogDto, contactId: number | null | undefined): boolean {
    if (contactId == null || call.contactId === contactId) return false;
    return weitereKontakte(call).some((k) => k.id === contactId);
}
